// @flow
import React from 'react';

import { useActiveTransfer } from './ResidentTransferCard/hooks';

/**
 * ENG-29435: injects the affordable programs carried by the TARGET floor plan of an in-progress
 * unit transfer, so the qualification-tab builder can union them with the current floor plan's.
 *
 * ResidentProfile is a class component and cannot call a hook where the tab builder runs, so the
 * query lives in this wrapper and arrives as a plain prop.
 *
 * Deliberately reuses `useActiveTransfer` rather than adding a query of its own: it is keyed on the
 * same react-query cache as the Resident Transfer card (activeTransferKey), so the two share one
 * request, and the card's start/edit/cancel/complete mutations already invalidate that key — the
 * program set therefore follows transfer state with no extra wiring.
 *
 * Must be applied INSIDE `connect` so `selectedProperty` and `residentInfo` are available.
 */

// Stable reference so a household with no active transfer does not hand the tab builder a new array
// identity on every render.
const NO_PROGRAMS = [];

// Only the fields this wrapper reads. Everything else is forwarded untouched, so the shape is
// inexact rather than an exhaustive mirror of ResidentProfile's props.
type OwnProps = {
  selectedProperty?: ?{ id?: ?string, organizationId?: ?string },
  residentInfo?: ?{ householdId?: ?string },
  flags?: ?{ unitTransfers?: ?boolean },
};

export const withTransferTargetPrograms = (
  WrappedComponent: React$ComponentType<mixed>,
): React$ComponentType<mixed> => {
  const WithTransferTargetPrograms = (props: OwnProps) => {
    const organizationId = props?.selectedProperty?.organizationId ?? '';
    const propertyId = props?.selectedProperty?.id ?? '';
    const householdId = props?.residentInfo?.householdId ?? '';
    // useActiveTransfer additionally requires all three ids before it fires.
    const enabled = Boolean(props?.flags?.unitTransfers);

    const { data: activeTransfer } = useActiveTransfer(
      organizationId,
      propertyId,
      householdId,
      enabled,
    );

    const transferTargetPrograms =
      activeTransfer?.details?.targetFloorPlanPrograms ?? NO_PROGRAMS;

    return (
      <WrappedComponent
        {...props}
        transferTargetPrograms={transferTargetPrograms}
      />
    );
  };

  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithTransferTargetPrograms.displayName = `withTransferTargetPrograms(${wrappedName})`;

  return WithTransferTargetPrograms;
};

export default withTransferTargetPrograms;
